/**
 * Organization Context
 *
 * Unified org context for the portal, serving both user types:
 * - Regular portal users: org comes from JWT (read-only)
 * - Super admins: org comes from impersonation selection (read-write)
 *
 * Replaces the isolated useAdminOrgContext() hook that caused
 * state synchronization bugs between OrgSwitcher and PortalLayout.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/auth/useAuth';
import { queryKeys } from '@/services/query/query-client';

// ============================================================================
// Types
// ============================================================================

export interface OrgContextValue {
  /** Active organization ID (from JWT for regular users, from selection for super admins) */
  orgId: string | null;
  /** Active organization display name */
  orgName: string | null;
  /** Whether the current org context is from super admin impersonation */
  isImpersonating: boolean;
  /** Set the active org (no-op for regular portal users) */
  setOrg: (id: string | null, name: string | null) => void;
  /** Clear org selection and return to admin (super admin only) */
  clearOrg: () => void;
}

// ============================================================================
// localStorage keys (kept for session persistence)
// ============================================================================

const STORAGE_KEY = 'curaition_admin_org_id';
const STORAGE_NAME_KEY = 'curaition_admin_org_name';

// ============================================================================
// Module-level getters for portalFetch (non-component function)
//
// Read localStorage directly so portalFetch can resolve the impersonated org
// synchronously, even on the very first request after a fresh page load —
// before the provider's React commit has run (CUR-617). The previous
// useEffect-driven module globals lost the impersonation context across the
// Stripe Checkout success_url redirect, causing portal API calls to fall
// back to the JWT's home org and serve the wrong tenant's data.
//
// Only super-admins ever write to ``STORAGE_KEY`` (``setOrg`` is a no-op
// otherwise), so presence in localStorage ⇒ impersonating.
// ============================================================================

/** Get current org ID for use in portalFetch headers */
// eslint-disable-next-line react-refresh/only-export-components -- non-component export alongside a component (affects dev fast-refresh only); file split is out of scope for CUR-1330
export function getCurrentOrgId(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

/** Check if current context is super admin impersonation */
// eslint-disable-next-line react-refresh/only-export-components -- non-component export alongside a component (affects dev fast-refresh only); file split is out of scope for CUR-1330
export function getIsImpersonating(): boolean {
  return localStorage.getItem(STORAGE_KEY) !== null;
}

// ============================================================================
// Context
// ============================================================================

const OrgContext = createContext<OrgContextValue | null>(null);

// ============================================================================
// Hook
// ============================================================================

// eslint-disable-next-line react-refresh/only-export-components -- non-component export alongside a component (affects dev fast-refresh only); file split is out of scope for CUR-1330
export function useOrgContext(): OrgContextValue {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrgContext must be used within an OrgContextProvider');
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

interface OrgContextProviderProps {
  children: ReactNode;
}

export function OrgContextProvider({ children }: OrgContextProviderProps) {
  const { isSuperAdmin, organizationId } = useAuth();
  const queryClient = useQueryClient();

  // Super admin: initialise from localStorage (persists across navigation)
  // Regular user: org comes from JWT, no localStorage involved
  const [impersonatedOrgId, setImpersonatedOrgId] = useState<string | null>(
    () => (isSuperAdmin ? localStorage.getItem(STORAGE_KEY) : null),
  );
  const [impersonatedOrgName, setImpersonatedOrgName] = useState<string | null>(
    () => (isSuperAdmin ? localStorage.getItem(STORAGE_NAME_KEY) : null),
  );

  // Derive the effective org context. The module-level getters
  // (``getCurrentOrgId`` / ``getIsImpersonating``) read localStorage directly
  // so we no longer need a useEffect to mirror state into module globals
  // (CUR-617). React state remains the source of truth for component renders;
  // ``setOrg`` writes to localStorage AND React state in lockstep.
  const isImpersonating = isSuperAdmin && impersonatedOrgId !== null;
  const orgId = isSuperAdmin ? impersonatedOrgId : (organizationId ?? null);
  const orgName = isSuperAdmin ? impersonatedOrgName : null;

  const setOrg = useCallback(
    (id: string | null, name: string | null) => {
      // No-op for regular portal users — their org is immutable
      if (!isSuperAdmin) return;

      if (id) {
        localStorage.setItem(STORAGE_KEY, id);
        localStorage.setItem(STORAGE_NAME_KEY, name || '');
      } else {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_NAME_KEY);
      }
      setImpersonatedOrgId(id);
      setImpersonatedOrgName(name);
      // Invalidate all portal queries so they refetch with new org header
      queryClient.invalidateQueries({ queryKey: ['portal'] });
      // Source-group data is also org-scoped (via the X-Organization-ID header
      // apiClient attaches to every request) but its query keys live under
      // ['sourceGroups', ...], outside the ['portal'] namespace, so it needs
      // its own invalidation here or a switched-to org sees the previous
      // org's groups served stale from cache.
      queryClient.invalidateQueries({ queryKey: queryKeys.sourceGroups });
    },
    [isSuperAdmin, queryClient],
  );

  const clearOrg = useCallback(() => {
    setOrg(null, null);
  }, [setOrg]);

  const value: OrgContextValue = {
    orgId,
    orgName,
    isImpersonating,
    setOrg,
    clearOrg,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}
